import DateDisplay from '@app/src/components/DateDisplay'
import AiSkippedQuestionInfo from '@app/src/components/Drawer/Views/AiSkippedQuestionInfo'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { ArrowForward, MessageOutlined } from '@mui/icons-material'
import type { ChipProps } from '@mui/material'
import { alpha, Box, Chip, Grid, Typography } from '@mui/material'
import type { Theme } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface SkippedQuestionCardProps {
  answerStatus: AnswerStatus
  estimatedAnswerAt?: ResponseItem['estimatedAnswerAt']
  isScraped?: ResponseItem['isScraped']
  comment?: ResponseItem['comment']
  chipVariant?: ChipProps['variant']
  aiChipColor?: boolean
}

const SkippedQuestionCard: React.FC<SkippedQuestionCardProps> = ({
  answerStatus,
  estimatedAnswerAt,
  isScraped,
  comment,
  chipVariant = 'outlined',
  aiChipColor = false,
}) => {
  const { formatMessage } = useIntl()

  const chipSx = useMemo(
    () =>
      !aiChipColor
        ? undefined
        : ({ palette }: Theme) => ({
            backgroundColor: palette.communication.light,
          }),
    [aiChipColor],
  )

  if (answerStatus === AnswerStatus.Excluded) {
    return (
      <Chip
        label={formatMessage({ id: 'templateBuilder.questionRules.excludedByQuestionRule' })}
        variant={chipVariant}
      />
    )
  }

  return (
    <Grid container color={({ palette }) => alpha(palette.common.black, 0.6)} spacing={1}>
      <Grid item container spacing={1} alignItems="flex-end">
        <Grid item>
          <Chip
            label={formatMessage({ id: 'reporting.skipQuestion.skipThisQuestion' })}
            variant={chipVariant}
            sx={chipSx}
          />
        </Grid>
        <Grid item>
          <Box display="flex" mb={0.5}>
            <ArrowForward color="inherit" />
          </Box>
        </Grid>
        <Grid item>
          <Chip
            label={formatMessage({ id: `reporting.answerStatus.${answerStatus}` })}
            variant={chipVariant}
            sx={chipSx}
          />
        </Grid>
        {estimatedAnswerAt && (
          <>
            <Grid item>
              <Box display="flex" mb={0.5}>
                <ArrowForward color="inherit" />
              </Box>
            </Grid>
            <Grid item>
              <Chip
                label={
                  <>
                    {`${formatMessage({ id: 'reporting.expectedAnswerAt' })} `}
                    <DateDisplay value={estimatedAnswerAt} />
                  </>
                }
                variant={chipVariant}
              />
            </Grid>
          </>
        )}
      </Grid>

      {isScraped ? (
        <Box ml={1} mt={1} width="100%">
          <AiSkippedQuestionInfo />
        </Box>
      ) : (
        comment && (
          <>
            <Grid
              item
              container
              spacing={1}
              my={1}
              color={({ palette }) => (comment ? 'inherit' : alpha(palette.common.black, 0.38))}
            >
              <Grid item>
                <MessageOutlined color="inherit" fontSize="small" />
              </Grid>
              <Grid item>
                <Typography color="inherit" variant="subtitle1">
                  {formatMessage({
                    id: 'reporting.skipQuestion.comment',
                  })}
                </Typography>
              </Grid>
            </Grid>
            <Grid item>
              <Typography variant="body1" color="text.primary">
                {comment}
              </Typography>
            </Grid>
          </>
        )
      )}
    </Grid>
  )
}

export default SkippedQuestionCard
